<template>
  <div class="crop-wrapper">
    <h2 class="section-title">GIF 裁剪</h2>

    <!-- 加载提示 -->
    <loading-tip 
      :visible="isLoading" 
      :text="loadingText" 
      :subtext="loadingSubtext"
      :progress="loadingProgress"
    />

    <!-- 上传区域 - 上传后隐藏 -->
    <label v-if="!hasGif" class="drop-area" :class="{ hover: isDragging }" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleDrop">
      拖拽或点击上传 GIF
      <div class="size-hint">{{ isMobile ? '建议不超过 8MB' : '建议不超过 20MB' }}</div>
      <input type="file" ref="gifInput" accept="image/gif" @change="handleFileChange">
    </label>

    <!-- 裁剪图片容器 -->
    <div v-show="hasGif" class="img-container">
      <img ref="cropImage" id="imageCrop" alt="请上传 GIF">
    </div>

    <!-- 裁剪参数控制 -->
    <div v-if="hasGif" class="controls">
      <div class="control-group">
        <label>X:</label>
        <input type="number" v-model.number="cropData.x" @change="applyCropData">
      </div>
      <div class="control-group">
        <label>Y:</label>
        <input type="number" v-model.number="cropData.y" @change="applyCropData">
      </div>
      <div class="control-group">
        <label>W:</label>
        <input type="number" v-model.number="cropData.width" @change="applyCropData">
      </div>
      <div class="control-group">
        <label>H:</label>
        <input type="number" v-model.number="cropData.height" @change="applyCropData">
      </div>
      <button @click="applyCropData">应用参数</button>
    </div>

    <!-- 比例预设 -->
    <div v-if="hasGif" class="preset-row">
      <button class="preset-btn" @click="applyPreset('free')">自由裁剪</button>
      <button class="preset-btn" @click="applyPreset('1:1')">1:1</button>
      <button class="preset-btn" @click="applyPreset('4:3')">4:3</button>
      <button class="preset-btn" @click="applyPreset('16:9')">16:9</button>
    </div>

    <!-- 操作按钮 -->
    <div v-if="hasGif" class="action-row">
      <button class="btn-primary" :disabled="isCropping" @click="startCrop">
        {{ isCropping ? '正在裁剪...' : '应用裁剪' }}
      </button>
      <button class="btn-download" :disabled="!croppedGifUrl" @click="downloadGif">下载 GIF</button>
      <button class="btn-reset" @click="resetAll">重置</button>
    </div>

    <!-- 进度条 -->
    <div class="progress-container" v-if="isCropping">
      <div class="progress-label">正在处理 GIF... {{ cropProgress }}%</div>
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: cropProgress + '%' }"></div>
      </div>
    </div>

    <!-- 裁剪结果预览 -->
    <div v-if="croppedGifUrl" class="result-section">
      <div class="result-title">裁剪结果预览</div>
      <img :src="croppedGifUrl" alt="裁剪后的 GIF" class="result-gif">
    </div>
  </div>
</template>

<script>
import SuperGif from '../lib/libgif.js'
import GIF from 'gif.js'
import LoadingTip from './LoadingTip.vue'
import { isMobile, getDeviceConfig, checkFileSize, formatFileSize, yieldToMain, downloadFile, blobToBase64 } from './utils.js'

export default {
  name: 'GifCrop',
  components: {
    LoadingTip
  },
  data() {
    const config = getDeviceConfig()
    return {
      gifFile: null,
      gifName: 'cropped',
      cropper: null,
      cropData: {
        x: 0,
        y: 0,
        width: 100,
        height: 100
      },
      isCropping: false,
      cropProgress: 0,
      croppedGifUrl: '',
      originalWidth: 0,
      originalHeight: 0,
      isDragging: false,
      hasGif: false,
      
      // 流式处理 - 不存储 base64，只存储帧信息
      superGif: null,
      totalFrames: 0,
      frameDelays: [],
      
      // 加载状态
      isLoading: false,
      loadingText: '',
      loadingSubtext: '',
      loadingProgress: -1,
      
      // 设备配置
      isMobile: config.isMobile,
      maxGifSize: config.maxGifSize,
      batchSize: config.batchSize
    }
  },
  methods: {
    handleDrop(e) {
      this.isDragging = false
      const file = e.dataTransfer.files[0]
      if (file && file.type === 'image/gif') {
        this.processFile(file)
      }
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    processFile(file) {
      // 文件大小检查
      const check = checkFileSize(file, this.maxGifSize)
      if (!check.valid) {
        this.$toast(check.message)
        return
      }
      
      this.gifFile = file
      this.gifName = file.name.replace(/\.gif$/i, '')
      this.croppedGifUrl = ''
      this.hasGif = true
      
      // 显示加载提示
      this.isLoading = true
      this.loadingText = '正在解析 GIF...'
      this.loadingSubtext = `文件大小: ${formatFileSize(file.size)}`
      this.loadingProgress = -1
      
      // 显示第一帧用于裁剪
      const url = URL.createObjectURL(file)
      const img = this.$refs.cropImage
      img.src = url
      
      img.onload = () => {
        if (this.cropper) {
          this.cropper.destroy()
        }
        
        this.cropper = new window.Cropper(img, {
          aspectRatio: NaN,
          viewMode: 1,
          autoCropArea: 0.8,
          crop: (event) => {
            this.cropData.x = Math.round(event.detail.x)
            this.cropData.y = Math.round(event.detail.y)
            this.cropData.width = Math.round(event.detail.width)
            this.cropData.height = Math.round(event.detail.height)
          }
        })
      }
      
      // 解析 GIF - 使用流式处理
      this.parseGifStream(file)
    },
    parseGifStream(file) {
      const wrapper = document.createElement('div')
      wrapper.style.cssText = 'position:fixed;left:-9999px;top:-9999px;'
      
      const gifImg = document.createElement('img')
      const blobUrl = URL.createObjectURL(file)
      gifImg.setAttribute('rel:animated_src', blobUrl)
      gifImg.setAttribute('rel:auto_play', '0')
      
      wrapper.appendChild(gifImg)
      document.body.appendChild(wrapper)
      
      const rub = new SuperGif({ gif: gifImg })
      rub.load(() => {
        this.totalFrames = rub.get_length()
        this.frameDelays = []
        
        const canvas = rub.get_canvas()
        this.originalWidth = canvas.width
        this.originalHeight = canvas.height
        
        // 只获取帧延迟信息，不存储帧数据
        const framesData = rub.get_frames()
        for (let i = 0; i < this.totalFrames; i++) {
          let delay = 100
          if (framesData && framesData[i] && framesData[i].delay) {
            delay = framesData[i].delay * 10
          }
          this.frameDelays.push(delay < 20 ? 100 : delay)
        }
        
        // 保存 SuperGif 实例用于后续处理
        this.superGif = rub
        this.gifWrapper = wrapper
        this.blobUrl = blobUrl
        
        this.isLoading = false
        this.loadingText = ''
        
        console.log(`GIF 解析完成: ${this.totalFrames} 帧, ${this.originalWidth}x${this.originalHeight}`)
      })
    },
    applyCropData() {
      if (this.cropper) {
        const x = parseInt(this.cropData.x) || 0
        const y = parseInt(this.cropData.y) || 0
        const w = parseInt(this.cropData.width) || 100
        const h = parseInt(this.cropData.height) || 100
        
        this.cropper.setData({ x, y, width: w, height: h })
      }
    },
    applyPreset(ratio) {
      if (!this.cropper) return
      
      if (ratio === 'free') {
        this.cropper.setAspectRatio(NaN)
      } else {
        const [w, h] = ratio.split(':').map(Number)
        this.cropper.setAspectRatio(w / h)
      }
    },
    async startCrop() {
      if (!this.superGif || this.totalFrames === 0) {
        this.$toast('请先上传 GIF')
        return
      }
      
      if (!this.cropper) {
        this.$toast('裁剪工具未初始化')
        return
      }
      
      this.isCropping = true
      this.cropProgress = 0
      this.croppedGifUrl = ''
      
      const data = this.cropper.getData()
      const x = Math.round(data.x)
      const y = Math.round(data.y)
      const width = Math.round(data.width)
      const height = Math.round(data.height)
      
      if (width <= 0 || height <= 0) {
        this.$toast('裁剪尺寸无效')
        this.isCropping = false
        return
      }
      
      const gif = new GIF({
        workers: 2,
        quality: this.isMobile ? 15 : 10,
        workerScript: './static/gifjs/gif.worker.js',
        width: width,
        height: height
      })
      
      gif.on('progress', (p) => {
        this.cropProgress = Math.round(50 + p * 50)
      })
      
      gif.on('finished', async (blob) => {
        // 使用data URL而不是blob URL，这样在小程序webview中可以长按保存
        this.croppedGifUrl = await blobToBase64(blob)
        this.isCropping = false
        this.cropProgress = 100
      })
      
      // 流式处理每一帧 - 分批处理避免卡顿
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = width
      tempCanvas.height = height
      const tempCtx = tempCanvas.getContext('2d')
      
      for (let i = 0; i < this.totalFrames; i++) {
        // 移动到当前帧
        this.superGif.move_to(i + 1)
        const frameCanvas = this.superGif.get_canvas()
        
        // 裁剪绘制
        tempCtx.clearRect(0, 0, width, height)
        tempCtx.drawImage(
          frameCanvas,
          x, y, width, height,
          0, 0, width, height
        )
        
        gif.addFrame(tempCanvas, { copy: true, delay: this.frameDelays[i] || 100 })
        
        this.cropProgress = Math.round((i + 1) / this.totalFrames * 50)
        
        // 每处理几帧让出主线程
        if (i % this.batchSize === 0) {
          await yieldToMain()
        }
      }
      
      // 释放临时 canvas
      tempCanvas.width = 1
      tempCanvas.height = 1
      
      gif.render()
    },
    async downloadGif() {
      if (!this.croppedGifUrl) return
      await downloadFile(this.croppedGifUrl, `${this.gifName}_cropped.gif`)
    },
    resetAll() {
      if (this.cropper) {
        this.cropper.destroy()
        this.cropper = null
      }
      
      // 释放 SuperGif 资源
      if (this.gifWrapper && this.gifWrapper.parentNode) {
        this.gifWrapper.parentNode.removeChild(this.gifWrapper)
      }
      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl)
      }
      
      const img = this.$refs.cropImage
      if (img) {
        img.src = ''
      }
      
      if (this.$refs.gifInput) {
        this.$refs.gifInput.value = ''
      }
      
      this.gifFile = null
      this.gifName = 'cropped'
      this.superGif = null
      this.totalFrames = 0
      this.frameDelays = []
      this.croppedGifUrl = ''
      this.cropData = { x: 0, y: 0, width: 100, height: 100 }
      this.isCropping = false
      this.cropProgress = 0
      this.hasGif = false
      this.isLoading = false
    }
  },
  beforeDestroy() {
    if (this.cropper) {
      this.cropper.destroy()
    }
    if (this.gifWrapper && this.gifWrapper.parentNode) {
      this.gifWrapper.parentNode.removeChild(this.gifWrapper)
    }
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl)
    }
    if (this.croppedGifUrl) {
      URL.revokeObjectURL(this.croppedGifUrl)
    }
  }
}
</script>

<style>
/* 不使用 scoped，确保 Cropper.js 样式正常工作 */
.crop-wrapper {
  padding: 8px 0;
}

.crop-wrapper .section-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2933;
}

.crop-wrapper .drop-area {
  display: flex;
  flex-direction: column;
  padding: 60px 30px;
  min-height: 180px;
  border: 2px dashed #9fb3ff;
  border-radius: 12px;
  background: #f8f9ff;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  font-size: 16px;
  color: #4f46e5;
  font-weight: 500;
}

.crop-wrapper .drop-area .size-hint {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 8px;
  font-weight: 400;
}

.crop-wrapper .drop-area.hover {
  border-color: #667eea;
  background: #e5edff;
}

.crop-wrapper .drop-area input[type="file"] {
  display: none;
}

.crop-wrapper .img-container {
  width: 100%;
  max-height: 350px;
  margin: 16px 0;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #f8f9ff;
}

.crop-wrapper .img-container img {
  display: block;
  max-width: 100%;
}

.crop-wrapper .controls {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}

.crop-wrapper .control-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.crop-wrapper .control-group label {
  font-weight: 500;
  color: #4b5563;
  font-size: 13px;
}

.crop-wrapper .control-group input[type="number"] {
  width: 55px;
  padding: 6px 4px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  text-align: center;
  font-size: 13px;
}

.crop-wrapper .control-group input[type="number"]:focus {
  outline: none;
  border-color: #667eea;
}

.crop-wrapper .controls button {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: #667eea;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.crop-wrapper .preset-row {
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.crop-wrapper .preset-btn {
  padding: 6px 14px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.crop-wrapper .preset-btn:hover {
  background: #667eea;
  color: #fff;
  border-color: #667eea;
}

.crop-wrapper .action-row {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.crop-wrapper .btn-primary {
  flex: 1;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.crop-wrapper .btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.crop-wrapper .btn-download {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #28a745;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.crop-wrapper .btn-download:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.crop-wrapper .btn-reset {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: #dc3545;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.crop-wrapper .progress-container {
  margin: 16px 0;
  padding: 12px;
  background: #f8f9ff;
  border-radius: 8px;
}

.crop-wrapper .progress-label {
  font-size: 13px;
  color: #4f46e5;
  font-weight: 500;
  margin-bottom: 8px;
  text-align: center;
}

.crop-wrapper .progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.crop-wrapper .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.crop-wrapper .result-section {
  margin-top: 16px;
  text-align: center;
}

.crop-wrapper .result-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #374151;
}

.crop-wrapper .result-gif {
  max-width: 100%;
  max-height: 250px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
}
</style>
